import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FileDown, Download, Loader2, Calendar, BarChart3, TrendingUp, TrendingDown, Clock, AlertCircle, ArrowLeft } from 'lucide-react';
import { sensorAPI } from '../utils/api';
import { useAuth } from '../utils/auth';

interface Generator {
  id: number;
  name: string;
  display_name?: string;
  device_id: string;
  device_name?: string;
  group_name: string;
}

interface ReportData {
  generator_info: {
    id: number;
    name: string;
    display_name?: string;
    device_id: string;
    device_name?: string;
    group_name: string;
  };
  period: {
    start_date: string;
    end_date: string;
    days: number;
  };
  summary: {
    total_fuel_decreased: number;
    total_fuel_increased: number;
    net_fuel_change: number;
    total_consumption: number;
    total_run_hours: number;
    total_on_off_cycles: number;
    total_decrease_alerts: number;
    total_increase_alerts: number;
    efficiency_metrics: {
      avg_fuel_per_hour: number;
      avg_fuel_per_cycle: number;
      avg_run_hours_per_day: number;
      avg_fuel_decreased_per_day: number;
      avg_fuel_increased_per_day: number;
    };
    alert_statistics: {
      avg_decrease_per_alert: number;
      avg_increase_per_alert: number;
      max_decrease_alert: number;
      max_increase_alert: number;
    };
    activity_days: {
      total_days: number;
      days_with_run_hours: number;
      days_with_decreases: number;
      days_with_increases: number;
      utilization_rate: number;
    };
  };
  daily_log: Array<{
    date: string;
    fuel_decreased: number;
    fuel_increased: number;
    net_fuel_change: number;
    run_hours: number;
    on_off_cycles: number;
    decrease_alerts_count: number;
    increase_alerts_count: number;
    fuel_per_hour: number;
    daily_consumption: number;
    consumption_per_hour: number | null;
    avg_decrease_per_alert: number;
    avg_increase_per_alert: number;
  }>;
}

const GeneratorReport: React.FC = () => {
  const { user } = useAuth();
  const [generators, setGenerators] = useState<Generator[]>([]);
  const [selectedGenerator, setSelectedGenerator] = useState<number | ''>('');
  const [periodType, setPeriodType] = useState<'preset' | 'custom'>('preset');
  const [presetPeriod, setPresetPeriod] = useState<string>('7days');
  const [customDays, setCustomDays] = useState<number>(7);
  const [customMonths, setCustomMonths] = useState<number>(1);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState<ReportData | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    fetchGenerators();
    // Set default dates
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 6);
    setCustomEndDate(today.toISOString().split('T')[0]);
    setCustomStartDate(weekAgo.toISOString().split('T')[0]);
  }, []);

  const fetchGenerators = async () => {
    try {
      setLoading(true);
      const response = await sensorAPI.getGenerators();
      if (response.generators) {
        setGenerators(response.generators);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch generators');
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    if (!selectedGenerator) {
      setError('Please select a generator');
      return;
    }

    try {
      setGenerating(true);
      setError('');
      
      let params: any = {};
      
      if (periodType === 'custom') {
        if (customStartDate && customEndDate) {
          params.start_date = customStartDate;
          params.end_date = customEndDate;
        } else if (customDays) {
          params.days = customDays;
        } else if (customMonths) {
          params.months = customMonths;
        }
      } else {
        params.period = presetPeriod;
      }

      const response = await sensorAPI.generateGeneratorReport(selectedGenerator, params);
      
      if (response.report) {
        setReport(response.report);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const exportToCSV = () => {
    if (!report) return;

    const csvRows: string[] = [];

    // Header
    csvRows.push('Generator Comprehensive Report');
    csvRows.push(`Generator: ${report.generator_info.display_name || report.generator_info.name}`);
    csvRows.push(`Device ID: ${report.generator_info.device_id}`);
    csvRows.push(`Device Name: ${report.generator_info.device_name || 'N/A'}`);
    csvRows.push(`Group: ${report.generator_info.group_name}`);
    csvRows.push(`Period: ${report.period.start_date} to ${report.period.end_date} (${report.period.days} days)`);
    csvRows.push('');

    // Summary Section
    csvRows.push('=== PERIOD SUMMARY ===');
    csvRows.push(`Total Fuel Decreased (L),${report.summary.total_fuel_decreased}`);
    csvRows.push(`Total Fuel Increased (L),${report.summary.total_fuel_increased}`);
    csvRows.push(`Net Fuel Change (L),${report.summary.net_fuel_change}`);
    csvRows.push(`Total Consumption (L),${report.summary.total_consumption || 0}`);
    csvRows.push(`Total Run Hours,${report.summary.total_run_hours}`);
    csvRows.push(`Total ON/OFF Cycles,${report.summary.total_on_off_cycles}`);
    csvRows.push(`Total Decrease Alerts,${report.summary.total_decrease_alerts}`);
    csvRows.push(`Total Increase Alerts,${report.summary.total_increase_alerts}`);
    csvRows.push('');

    // Efficiency Metrics
    csvRows.push('=== EFFICIENCY METRICS ===');
    csvRows.push(`Average Fuel per Hour (L/hr),${report.summary.efficiency_metrics.avg_fuel_per_hour}`);
    csvRows.push(`Average Fuel per Cycle (L/cycle),${report.summary.efficiency_metrics.avg_fuel_per_cycle}`);
    csvRows.push(`Average Run Hours per Day,${report.summary.efficiency_metrics.avg_run_hours_per_day}`);
    csvRows.push(`Average Fuel Decreased per Day (L),${report.summary.efficiency_metrics.avg_fuel_decreased_per_day}`);
    csvRows.push(`Average Fuel Increased per Day (L),${report.summary.efficiency_metrics.avg_fuel_increased_per_day}`);
    csvRows.push('');

    // Alert Statistics
    csvRows.push('=== ALERT STATISTICS ===');
    csvRows.push(`Average Decrease per Alert (L),${report.summary.alert_statistics.avg_decrease_per_alert}`);
    csvRows.push(`Average Increase per Alert (L),${report.summary.alert_statistics.avg_increase_per_alert}`);
    csvRows.push(`Max Decrease Alert (L),${report.summary.alert_statistics.max_decrease_alert}`);
    csvRows.push(`Max Increase Alert (L),${report.summary.alert_statistics.max_increase_alert}`);
    csvRows.push('');

    // Activity Days
    csvRows.push('=== ACTIVITY STATISTICS ===');
    csvRows.push(`Total Days,${report.summary.activity_days.total_days}`);
    csvRows.push(`Days with Run Hours,${report.summary.activity_days.days_with_run_hours}`);
    csvRows.push(`Days with Decreases,${report.summary.activity_days.days_with_decreases}`);
    csvRows.push(`Days with Increases,${report.summary.activity_days.days_with_increases}`);
    csvRows.push(`Utilization Rate (%),${report.summary.activity_days.utilization_rate}`);
    csvRows.push('');

    // Daily Log
    csvRows.push('=== DAILY LOG ===');
    csvRows.push('Date,Fuel Decreased (L),Fuel Increased (L),Net Change (L),Consumption (L),Run Hours,ON/OFF Cycles,Decrease Alerts,Increase Alerts,Fuel per Hour (L/hr),Consumption per Hour (L/hr),Avg Decrease/Alert (L),Avg Increase/Alert (L)');
    
    report.daily_log.forEach(day => {
      csvRows.push([
        day.date,
        day.fuel_decreased,
        day.fuel_increased,
        day.net_fuel_change,
        day.daily_consumption || 0,
        day.run_hours,
        day.on_off_cycles,
        day.decrease_alerts_count,
        day.increase_alerts_count,
        day.fuel_per_hour,
        day.consumption_per_hour || (day.run_hours > 0 && day.daily_consumption ? (day.daily_consumption / day.run_hours).toFixed(2) : '0.00'),
        day.avg_decrease_per_alert,
        day.avg_increase_per_alert,
      ].join(','));
    });

    // Create and download
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `generator_report_${report.generator_info.name}_${report.period.start_date}_to_${report.period.end_date}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <motion.header
        className="bg-white shadow-sm border-b border-gray-200"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Link
                to="/"
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                title="Back to Dashboard"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </Link>
              <BarChart3 className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Generator Comprehensive Report</h1>
                <p className="text-sm text-gray-500">Welcome back, {user?.name}</p>
              </div>
            </div>
          </div>
        </div>
      </motion.header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg p-6"
        >

        {/* Generator Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Generator
          </label>
          <select
            value={selectedGenerator}
            onChange={(e) => setSelectedGenerator(e.target.value ? Number(e.target.value) : '')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            disabled={loading}
          >
            <option value="">-- Select Generator --</option>
            {generators.map((gen) => {
              // Format: ACC (أبو عيد) - Device: أبو عيد
              const displayText = gen.device_name 
                ? `${gen.name} (${gen.device_name}) - Device: ${gen.device_name}`
                : gen.display_name || gen.name;
              return (
                <option key={gen.id} value={gen.id}>
                  {displayText} {gen.group_name && `(${gen.group_name})`}
                </option>
              );
            })}
          </select>
        </div>

        {/* Period Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Report Period
          </label>
          <div className="flex space-x-4 mb-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="preset"
                checked={periodType === 'preset'}
                onChange={(e) => setPeriodType(e.target.value as 'preset')}
                className="mr-2"
              />
              Preset Period
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="custom"
                checked={periodType === 'custom'}
                onChange={(e) => setPeriodType(e.target.value as 'custom')}
                className="mr-2"
              />
              Custom Period
            </label>
          </div>

          {periodType === 'preset' ? (
            <select
              value={presetPeriod}
              onChange={(e) => setPresetPeriod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
              <option value="1month">Last Month</option>
              <option value="3months">Last 3 Months</option>
              <option value="6months">Last 6 Months</option>
              <option value="1year">Last Year</option>
            </select>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Days</label>
                <input
                  type="number"
                  value={customDays}
                  onChange={(e) => {
                    setCustomDays(Number(e.target.value));
                    setCustomMonths(0);
                    setCustomStartDate('');
                    setCustomEndDate('');
                  }}
                  min="1"
                  max="365"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Days"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Months</label>
                <input
                  type="number"
                  value={customMonths}
                  onChange={(e) => {
                    setCustomMonths(Number(e.target.value));
                    setCustomDays(0);
                    setCustomStartDate('');
                    setCustomEndDate('');
                  }}
                  min="1"
                  max="12"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Months"
                />
              </div>
              <div className="md:col-span-2 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => {
                      setCustomStartDate(e.target.value);
                      setCustomDays(0);
                      setCustomMonths(0);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => {
                      setCustomEndDate(e.target.value);
                      setCustomDays(0);
                      setCustomMonths(0);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Generate Button */}
        <div className="mb-6">
          <button
            onClick={generateReport}
            disabled={generating || !selectedGenerator}
            className="w-full md:w-auto px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Generating Report...</span>
              </>
            ) : (
              <>
                <BarChart3 className="w-5 h-5" />
                <span>Generate Report</span>
              </>
            )}
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
            {error}
          </div>
        )}

        {/* Report Display */}
        {report && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Report Results</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {report.generator_info.device_name 
                    ? `${report.generator_info.name} (${report.generator_info.device_name}) - Device: ${report.generator_info.device_name}`
                    : report.generator_info.display_name || report.generator_info.name
                  }
                </p>
              </div>
              <button
                onClick={exportToCSV}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Export CSV</span>
              </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Fuel Decreased</p>
                    <p className="text-2xl font-bold text-blue-600">{report.summary.total_fuel_decreased} L</p>
                  </div>
                  <TrendingDown className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Fuel Increased</p>
                    <p className="text-2xl font-bold text-green-600">{report.summary.total_fuel_increased} L</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Consumption</p>
                    <p className="text-2xl font-bold text-indigo-600">{report.summary.total_consumption || 0} L</p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-indigo-600" />
                </div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Run Hours</p>
                    <p className="text-2xl font-bold text-purple-600">{report.summary.total_run_hours} hrs</p>
                  </div>
                  <Clock className="w-8 h-8 text-purple-600" />
                </div>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Fuel per Hour</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {report.summary.total_run_hours > 0 
                        ? ((report.summary.total_consumption || report.summary.total_fuel_decreased) / report.summary.total_run_hours).toFixed(2)
                        : report.summary.efficiency_metrics.avg_fuel_per_hour
                      } L/hr
                    </p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-orange-600" />
                </div>
              </div>
            </div>

            {/* Detailed Summary Table */}
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-2 text-sm text-gray-700">Net Fuel Change</td>
                    <td className="px-4 py-2 text-sm font-medium">{report.summary.net_fuel_change} L</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm text-gray-700">Total Consumption</td>
                    <td className="px-4 py-2 text-sm font-medium">{report.summary.total_consumption || 0} L</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm text-gray-700">Total ON/OFF Cycles</td>
                    <td className="px-4 py-2 text-sm font-medium">{report.summary.total_on_off_cycles}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm text-gray-700">Total Decrease Alerts</td>
                    <td className="px-4 py-2 text-sm font-medium">{report.summary.total_decrease_alerts}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm text-gray-700">Total Increase Alerts</td>
                    <td className="px-4 py-2 text-sm font-medium">{report.summary.total_increase_alerts}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm text-gray-700">Average Fuel per Hour</td>
                    <td className="px-4 py-2 text-sm font-medium">
                      {report.summary.total_run_hours > 0 
                        ? (report.summary.total_fuel_decreased / report.summary.total_run_hours).toFixed(2)
                        : report.summary.efficiency_metrics.avg_fuel_per_hour
                      } L/hr
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm text-gray-700">Average Fuel per Cycle</td>
                    <td className="px-4 py-2 text-sm font-medium">{report.summary.efficiency_metrics.avg_fuel_per_cycle} L/cycle</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm text-gray-700">Utilization Rate</td>
                    <td className="px-4 py-2 text-sm font-medium">{report.summary.activity_days.utilization_rate}%</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Daily Log Table */}
            <div className="overflow-x-auto">
              <h3 className="text-lg font-semibold mb-3">Daily Log</h3>
              <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fuel ↓ (L)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fuel ↑ (L)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Net (L)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consumption (L)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Run Hours</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cycles</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">L/hr</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {report.daily_log.map((day, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm">{day.date}</td>
                      <td className="px-4 py-2 text-sm text-red-600">{day.fuel_decreased}</td>
                      <td className="px-4 py-2 text-sm text-green-600">{day.fuel_increased}</td>
                      <td className="px-4 py-2 text-sm font-medium">{day.net_fuel_change}</td>
                      <td className="px-4 py-2 text-sm text-indigo-600 font-medium">
                        {day.daily_consumption || '0.00'}
                      </td>
                      <td className="px-4 py-2 text-sm">{day.run_hours}</td>
                      <td className="px-4 py-2 text-sm">{day.on_off_cycles}</td>
                      <td className="px-4 py-2 text-sm">
                        {day.run_hours > 0 
                          ? ((day.daily_consumption || day.fuel_decreased) / day.run_hours).toFixed(2)
                          : day.fuel_per_hour || '0.00'
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
        </motion.div>
      </div>
    </div>
  );
};

export default GeneratorReport;

